import client from '../http_client';
import {
  AvailableForecasts,
  MapDefinition,
  DataSource,
  VariableDataFrame,
  CachedFrame,
  AvailableData,
} from './types';
import { textureLoadingService } from './texture-loading-service';

const root = '/v1/mapping_v2';

export class MappingService {
  public static async getAvailableForecasts(demo?: boolean): Promise<AvailableForecasts> {
    let base = root;
    if (demo) {
      base = `${root}/demo`;
    }
    const url = `${base}/available-forecasts`;
    const response = await client.get<AvailableForecasts>(url);
    return response.data;
  }

  public static readonly availableMaps: MapDefinition[] = [
    {
      id: 1,
      title: 'Air Temperature @ 2m',
      primaryVariable: 'air_temperature_at_height_level_2m',
      settings: {
        layers: [
          {
            enabled: true,
            id: 'highlow_pressure',
            opacity: 1,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'HIGHLOW',
            radius: 2000,
            textSize: 14,
            textOutlineWidth: 1,
            textColor: '#ffffff',
            textOutlineColor: '#ff0000',
            legend: {
              title: 'Pressure (High/Low)',
              unitFormat: {
                unit: 'hPa',
                scale: 0.01,
              },
            },
            tooltip: {
              show: true,
              title: 'Pressure',
            },
          },
          {
            enabled: true,
            id: 'raster_temperature',
            opacity: 0.5,
            interpolation: 'LINEAR',
            variable: 'air_temperature_at_height_level_2m',
            kind: 'RASTER',
            palette: {
              kind: 'union',
              id: 'union',
              palettes: [
                {
                  kind: 'sns',
                  id: 'Blues',
                },
                {
                  kind: 'sns',
                  id: 'Spectral_r_with_pink',
                },
              ],
            },
            colorScaleMin: 223.15,
            colorScaleMax: 323.15,
            legend: {
              title: 'Air Temperature @ 2m',
              unitFormat: {
                unit: '°C',
                offset: -273.15,
              },
              show: true,
            },
            tooltip: {
              show: true,
              title: 'Temperature',
            },
          },
          {
            enabled: true,
            id: 'raster_cloud',
            opacity: 0.6,
            interpolation: 'LINEAR',
            variable: 'cloud_area_fraction_at_entire_atmosphere',
            kind: 'RASTER',
            palette: {
              kind: 'color_alpha_scale',
              id: 'color_alpha',
              color: '#ffffff',
            },
            colorScaleMin: 0.0,
            colorScaleMax: 1.0,
          },
          {
            enabled: true,
            id: 'raster_precipitation',
            opacity: 1,
            interpolation: 'LINEAR',
            variable: 'precipitation_amount_sum_1h',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'Spectral_r_a0',
            },
            colorScaleMin: 0.001,
            colorScaleMax: 15,
            legend: {
              title: 'Precipitation 1hr',
              unitFormat: {
                unit: 'mm',
                scale: 1.0,
              },
              show: true,
            },
          },
          {
            enabled: true,
            id: 'particle_wind_10m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_10m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 10m',
            },
            legend: {
              title: 'Wind speed @ 10m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'particle_wind_100m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_100m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 100m',
            },
            legend: {
              title: 'Wind speed @ 100m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'contour_pressure',
            opacity: 0.5,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'CONTOUR',
            interval: 400,
            majorInterval: 2000,
            width: 1,
            color: '#ffffff',
          },
        ],
        toggles: [
          {
            title: 'Pressure',
            layers: { on: ['highlow_pressure', 'contour_pressure'] },
            default: false,
          },
          {
            title: 'Cloud Cover',
            layers: { on: ['raster_cloud'] },
            default: false,
          },
          {
            title: 'Precipitation',
            layers: { on: ['raster_precipitation'] },
            default: false,
          },
          {
            title: 'Wind @ 10m',
            layers: {
              on: ['particle_wind_10m'],
              off: ['particle_wind_100m'],
            },
            default: false,
          },
          {
            title: 'Wind @ 100m',
            layers: {
              on: ['particle_wind_100m'],
              off: ['particle_wind_10m'],
            },
            default: false,
          },
        ],
      },
    },
    {
      id: 2,
      title: 'Air Pressure @ Sea Level',
      primaryVariable: 'air_pressure_at_mean_sea_level',
      settings: {
        layers: [
          {
            enabled: true,
            id: 'highlow_pressure',
            opacity: 1,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'HIGHLOW',
            radius: 2000,
            textSize: 14,
            textOutlineWidth: 1,
            textColor: '#ffffff',
            textOutlineColor: '#ff0000',
            legend: {
              title: 'Pressure (High/Low)',
              unitFormat: {
                unit: 'hPa',
                scale: 0.01,
              },
            },
          },
          {
            enabled: true,
            id: 'raster_air_pressure',
            opacity: 0.4,
            interpolation: 'LINEAR',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'nipy_spectral',
            },
            colorScaleMin: 96000,
            colorScaleMax: 104000,
            legend: {
              title: 'Air Pressure @ Mean Sea Level',
              unitFormat: {
                unit: 'hPa',
                scale: 0.01,
              },
              show: true,
            },
          },
          {
            enabled: true,
            id: 'raster_cloud',
            opacity: 0.6,
            interpolation: 'LINEAR',
            variable: 'cloud_area_fraction_at_entire_atmosphere',
            kind: 'RASTER',
            palette: {
              kind: 'color_alpha_scale',
              id: 'color_alpha',
              color: '#ffffff',
            },
            colorScaleMin: 0.0,
            colorScaleMax: 1.0,
          },
          {
            enabled: true,
            id: 'raster_precipitation',
            opacity: 1,
            interpolation: 'LINEAR',
            variable: 'precipitation_amount_sum_1h',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'Spectral_r_a0',
            },
            colorScaleMin: 0.001,
            colorScaleMax: 15,
            legend: {
              title: 'Precipitation 1hr',
              unitFormat: {
                unit: 'mm',
                scale: 1.0,
              },
              show: true,
            },
          },
          {
            enabled: true,
            id: 'particle_wind_10m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_10m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 10m',
            },
            legend: {
              title: 'Wind speed @ 10m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'particle_wind_100m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_100m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 100m',
            },
            legend: {
              title: 'Wind speed @ 100m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'contour_pressure',
            opacity: 0.5,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'CONTOUR',
            interval: 200,
            majorInterval: 2000,
            width: 1,
            color: '#ffffff',
          },
        ],
        toggles: [
          {
            title: 'Cloud Cover',
            layers: { on: ['raster_cloud'] },
            default: false,
          },
          {
            title: 'Precipitation',
            layers: { on: ['raster_precipitation'] },
            default: false,
          },
          {
            title: 'Wind @ 10m',
            layers: {
              on: ['particle_wind_10m'],
              off: ['particle_wind_100m'],
            },
            default: false,
          },
          {
            title: 'Wind @ 100m',
            layers: {
              on: ['particle_wind_100m'],
              off: ['particle_wind_10m'],
            },
            default: false,
          },
        ],
      },
    },
    {
      id: 3,
      title: 'Geopotential Height @ 500hPa',
      primaryVariable: 'geopotential_at_pressure_level_50000Pa',
      settings: {
        layers: [
          {
            enabled: true,
            id: 'raster_geopot',
            opacity: 0.66,
            interpolation: 'LINEAR',
            variable: 'geopotential_at_pressure_level_50000Pa',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'geopotential_rainbow',
            },
            legend: {
              title: 'Geopotential Height @ 500hPa',
              unitFormat: {
                unit: 'gpm',
                scale: 1.0 / 9.80665,
                decimals: 0,
              },
              show: true,
            },
            colorScaleMin: 4800.0 * 9.80665,
            colorScaleMax: 6000.0 * 9.80665,
            tooltip: {
              show: true,
              title: 'Geopotential Height',
            },
          },
          {
            enabled: true,
            id: 'raster_cloud',
            opacity: 0.6,
            interpolation: 'LINEAR',
            variable: 'cloud_area_fraction_at_entire_atmosphere',
            kind: 'RASTER',
            palette: {
              kind: 'color_alpha_scale',
              id: 'color_alpha',
              color: '#ffffff',
            },
            colorScaleMin: 0.0,
            colorScaleMax: 1.0,
          },
          {
            enabled: true,
            id: 'raster_precipitation',
            opacity: 1,
            interpolation: 'LINEAR',
            variable: 'precipitation_amount_sum_1h',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'Spectral_r_a0',
            },
            colorScaleMin: 0.001,
            colorScaleMax: 15,
            legend: {
              title: 'Precipitation 1hr',
              unitFormat: {
                unit: 'mm',
                scale: 1.0,
              },
              show: true,
            },
          },
          {
            enabled: true,
            id: 'particle_wind_10m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_10m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 10m',
            },
            legend: {
              title: 'Wind speed @ 10m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'particle_wind_100m',
            opacity: 0.3,
            interpolation: 'CUBIC',
            variable: 'wind_vector_at_height_level_100m',
            kind: 'PARTICLE',
            numParticles: 4000,
            speedFactor: 20,
            maxAge: 6,
            animate: true,
            color: '#ffffff',
            width: 1,
            tooltip: {
              show: true,
              title: 'Wind Speed @ 100m',
            },
            legend: {
              title: 'Wind speed @ 100m',
              unitFormat: {
                unit: 'm/s',
              },
            },
          },
          {
            enabled: true,
            id: 'contour_pressure',
            opacity: 0.5,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'CONTOUR',
            interval: 200,
            majorInterval: 2000,
            width: 1,
            color: '#ffffff',
          },
        ],
        toggles: [
          {
            title: 'Air Pressure',
            layers: { on: ['contour_pressure'] },
            default: true,
          },
          {
            title: 'Cloud Cover',
            layers: { on: ['raster_cloud'] },
            default: false,
          },
          {
            title: 'Precipitation',
            layers: { on: ['raster_precipitation'] },
            default: false,
          },
          {
            title: 'Wind @ 10m',
            layers: {
              on: ['particle_wind_10m'],
              off: ['particle_wind_100m'],
            },
            default: false,
          },
          {
            title: 'Wind @ 100m',
            layers: {
              on: ['particle_wind_100m'],
              off: ['particle_wind_10m'],
            },
            default: false,
          },
        ],
      },
    },
    {
      id: 4,
      title: 'Surface Solar Radiation',
      primaryVariable: 'surface_downwelling_shortwave_flux_sum_1h',
      settings: {
        layers: [
          {
            enabled: true,
            id: 'raster_solar_radiation',
            opacity: 0.7,
            interpolation: 'LINEAR',
            variable: 'surface_downwelling_shortwave_flux_sum_1h',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'magma',
            },
            colorScaleMin: 0,
            colorScaleMax: 3600000,
            legend: {
              title: 'Surface Solar Radiation',
              unitFormat: {
                unit: 'MJ m⁻²',
                scale: 0.000001,
                decimals: 1,
              },
              show: true,
            },
            tooltip: {
              show: true,
              title: 'Solar Radiation',
            },
          },
          {
            enabled: true,
            id: 'highlow_pressure',
            opacity: 1,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'HIGHLOW',
            radius: 2000,
            textSize: 14,
            textOutlineWidth: 1,
            textColor: '#ffffff',
            textOutlineColor: '#ff0000',
            legend: {
              title: 'Pressure (High/Low)',
              unitFormat: {
                unit: 'hPa',
                scale: 0.01,
              },
            },
            tooltip: {
              show: true,
              title: 'Pressure',
            },
          },
          {
            enabled: true,
            id: 'contour_pressure',
            opacity: 0.5,
            interpolation: 'CUBIC',
            variable: 'air_pressure_at_mean_sea_level',
            kind: 'CONTOUR',
            interval: 400,
            majorInterval: 2000,
            width: 1,
            color: '#ffffff',
          },
          {
            enabled: true,
            id: 'raster_cloud',
            opacity: 0.6,
            interpolation: 'LINEAR',
            variable: 'cloud_area_fraction_at_entire_atmosphere',
            kind: 'RASTER',
            palette: {
              kind: 'color_alpha_scale',
              id: 'color_alpha',
              color: '#ffffff',
            },
            colorScaleMin: 0.0,
            colorScaleMax: 1.0,
          },
          {
            enabled: true,
            id: 'raster_precipitation',
            opacity: 1,
            interpolation: 'LINEAR',
            variable: 'precipitation_amount_sum_1h',
            kind: 'RASTER',
            palette: {
              kind: 'sns',
              id: 'Spectral_r_a0',
            },
            colorScaleMin: 0.001,
            colorScaleMax: 15,
            legend: {
              title: 'Precipitation 1hr',
              unitFormat: {
                unit: 'mm',
                scale: 1.0,
              },
              show: true,
            },
          },
        ],
        toggles: [
          {
            title: 'Pressure',
            layers: { on: ['highlow_pressure', 'contour_pressure'] },
            default: false,
          },
          {
            title: 'Cloud Cover',
            layers: { on: ['raster_cloud'] },
            default: false,
          },
          {
            title: 'Precipitation',
            layers: { on: ['raster_precipitation'] },
            default: false,
          },
        ],
      },
    },
  ];

  /**
   * Load texture data for a single variable
   * @param leadTime - prediction_timedelta in minutes (e.g., 60, 360, 720, 1440...)
   */
  public static async loadVariableData(
    dataSource: DataSource,
    leadTime: number,
    variable: string,
  ): Promise<VariableDataFrame> {
    return textureLoadingService.loadVariableData(dataSource, leadTime, variable);
  }

  /**
   * Load texture data for multiple variables
   * @param leadTime - prediction_timedelta in minutes (e.g., 60, 360, 720, 1440...)
   */
  public static async loadFrameData(
    dataSource: DataSource,
    leadTime: number,
    variables: string[],
  ): Promise<CachedFrame> {
    return textureLoadingService.loadFrameData(dataSource, leadTime, variables);
  }

  public static async loadAvailableData(
    dataSource: DataSource,
    demo?: boolean,
  ): Promise<AvailableData> {
    let base = root;
    if (demo) {
      base = `${root}/demo`;
    }
    const url = `${base}/available-data`;
    const params = new URLSearchParams({
      model: dataSource.model,
      forecast: dataSource.forecast,
    });

    const response = await client.get<AvailableData>(`${url}?${params}`);
    return response.data;
  }
}
